import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import block from 'bem-cn';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import SVG from 'components/SVG';
import { checkDomainsAllowed } from 'features/bonusProgram/data';
import { actions } from 'features/domains/redux';

import crossSVG from 'shared/img/cross-transparent.svg';

import rocketSVG from './img/rocket.svg';

import './DomainFeaturePopup.scss';

const b = block('domain-feature-popup');

const storageKeyPrefix = 'domainsFeaturePopupHidden';

const getStorageKey = agentId => `${storageKeyPrefix}:${agentId}`;

const DomainFeaturePopup = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const locale = useSelector(state => state.locale.locale);
  const authData = useSelector(state => state.auth);
  const [isOpen, setIsOpen] = useState(false);

  const isAllowed = checkDomainsAllowed(authData);
  const storageKey = useMemo(() => (authData?.id ? getStorageKey(authData.id) : null), [authData?.id]);

  const hidePopup = useCallback(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, 'true');
    }

    setIsOpen(false);
  }, [storageKey]);

  const handleDomainsClick = useCallback(() => {
    hidePopup();
    history.push('/domains');
  }, [hidePopup, history]);

  useEffect(() => {
    if (!authData?.isAuth || !isAllowed || !storageKey || localStorage.getItem(storageKey) === 'true') {
      setIsOpen(false);
      return;
    }

    let isActual = true;

    dispatch(actions.getDomains()).then(response => {
      if (!isActual || !response?.success) return;

      if (response.data?.domainRequests?.length) {
        localStorage.setItem(storageKey, 'true');
        setIsOpen(false);
        return;
      }

      setIsOpen(true);
    });

    return () => {
      isActual = false;
    };
  }, [authData?.isAuth, dispatch, isAllowed, storageKey]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        hidePopup();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hidePopup, isOpen]);

  if (!isOpen) return null;

  const layout = (
    <div className={b()} role="presentation">
      <div className={b('card')} role="dialog" aria-modal="true" aria-labelledby="domain-feature-popup-title">
        <div className={b('header')}>
          <div id="domain-feature-popup-title" className={b('title')}>
            {locale.domainFeaturePopupTitle}
          </div>
          <button className={b('close')} type="button" onClick={hidePopup} aria-label={locale.close}>
            <SVG svgProps={{ svg: crossSVG }} className={b('close-icon')} />
          </button>
        </div>
        <div className={b('body')}>
          <SVG svgProps={{ svg: rocketSVG }} className={b('rocket')} />
          <div className={b('subtitle')}>{locale.domainFeaturePopupSubtitle}</div>
          <div className={b('text')}>{locale.domainFeaturePopupText1}</div>
          <div className={b('text')}>{locale.domainFeaturePopupText2}</div>
          <button className={b('button')} type="button" onClick={handleDomainsClick}>
            {locale.domains}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(layout, document.getElementById('modal-root'));
};

export default DomainFeaturePopup;
